import type {DecodedImage, ImageCodec} from "./imageCompression";
import {HEADER_PROBE_BYTES, longestEdge, readImageDimensions, type ImageDimensions} from "./imageHeader";

/**
 * The browser half of the compression pipeline (ui#601): `createImageBitmap` to decode and an
 * `OffscreenCanvas` to scale, with the ENCODE step left injectable (see `webpEncoders.ts`).
 *
 * Both APIs work on a worker thread as well as the main one, which is the whole reason the pipeline can
 * be moved off the main thread without a second implementation.
 *
 * This module is never imported by a test: jsdom has neither API, so everything here is exercised by the
 * committed e2e instead. That is deliberate rather than a gap — the decisions worth asserting live in
 * `imageCompression.ts` behind the seam this file implements, and a mock of `createImageBitmap` would
 * only assert that the mock was called.
 */

/** What an encoder is handed: a canvas already holding the scaled picture, plus how hard to squeeze. */
export interface EncodeRequest {
	/** The picture at its final size. An encoder may read pixels from it but must not resize it. */
	canvas: OffscreenCanvas;
	/** 0–100, libwebp's scale. */
	quality: number;
	/**
	 * Whether any pixel is non-opaque, computed on demand and memoised.
	 *
	 * It is a function rather than a value because scanning a 1920×1080 picture is two million reads
	 * and only ONE encoder ever needs the answer — the canvas fallback, deciding whether it is allowed
	 * to substitute a format that has no alpha channel.
	 */
	hasAlpha: () => boolean;
}

/** Encodes the canvas. Must throw rather than return a format that loses information silently. */
export type CanvasEncoder = (request: EncodeRequest) => Promise<Blob>;

/** Builds the browser codec around an encoder. */
export function offscreenImageCodec(encode: CanvasEncoder): ImageCodec {
	return {
		measure: measureImage,
		async decode(file, capEdgePixels) {
			const bitmap = await createImageBitmap(file, await decodeOptions(file, capEdgePixels));
			return decodedImage(bitmap, encode);
		},
	};
}

/** Reads a file's intrinsic size from its head, without decoding a single pixel. */
export async function measureImage(file: Blob): Promise<ImageDimensions | null> {
	try {
		const head = await file.slice(0, HEADER_PROBE_BYTES).arrayBuffer();
		return readImageDimensions(new Uint8Array(head));
	} catch {
		// A head that cannot be read is not a reason to fail the pick — the decoder gets its own attempt,
		// and it is the one that decides whether these bytes are an image at all.
		return null;
	}
}

/**
 * Decode options that bound MEMORY, which is the difference between shrinking a 100-megapixel photo and
 * crashing the tab trying to.
 *
 * `createImageBitmap` materialises RGBA — four bytes per pixel regardless of how well the file
 * compressed — so a 12000×9000 source is a 432 MB allocation, and mobile Safari does not survive it.
 * Passing ONE of `resizeWidth`/`resizeHeight` makes the browser scale during decode and never hold the
 * full-size bitmap; the other axis follows by aspect ratio, per spec.
 *
 * Two rules make that safe. The hint is only applied to the LONGER axis, so the shorter one cannot
 * overshoot the cap. And it is only applied when the picture is genuinely larger than the cap: these
 * hints will happily ENLARGE, and upscaling a 700 px logo to 1920 px would waste bytes to make it
 * blurrier. When the size cannot be read at all, no hint is sent and the browser decodes at full size —
 * the memory bound is lost, but the pick still works, which is the right way round.
 *
 * The hint is a BOUND, not a promise. A header's axes are pre-orientation, so a phone photo tagged
 * Exif 6 or 8 decodes with its axes swapped and the hint can land on what became the shorter one,
 * leaving a bitmap over the cap. That costs memory, never correctness: the pipeline scales from the
 * size the bitmap ACTUALLY has, never from the header's.
 */
async function decodeOptions(file: Blob, capEdgePixels: number): Promise<ImageBitmapOptions> {
	// `from-image` honours the Exif orientation flag. Without it a phone photo re-encodes sideways,
	// because the flag lives in metadata that the re-encode does not carry over.
	const options: ImageBitmapOptions = {imageOrientation: "from-image"};
	const dimensions = await measureImage(file);
	if (!dimensions || longestEdge(dimensions) <= capEdgePixels) {
		return options;
	}
	return dimensions.width >= dimensions.height
		? {...options, resizeWidth: capEdgePixels, resizeQuality: "high"}
		: {...options, resizeHeight: capEdgePixels, resizeQuality: "high"};
}

/**
 * Wraps a decoded bitmap so the pipeline can re-encode it at any size.
 *
 * ONE canvas is reused across every attempt: the quality search encodes six times per size, and a fresh
 * canvas each time would be six more allocations of the same pixels. Assigning `width`/`height` resizes
 * AND clears it, which is exactly the reset each attempt needs.
 */
function decodedImage(bitmap: ImageBitmap, encode: CanvasEncoder): DecodedImage {
	const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
	const context = canvas.getContext("2d");
	if (!context) {
		bitmap.close();
		throw new Error("This browser gave no 2d context for image compression");
	}
	let alpha: boolean | undefined;

	return {
		width: bitmap.width,
		height: bitmap.height,
		async encode(width, height, quality) {
			canvas.width = width;
			canvas.height = height;
			context.imageSmoothingQuality = "high";
			context.drawImage(bitmap, 0, 0, width, height);
			return encode({
				canvas,
				quality,
				hasAlpha: () => {
					// Memoised across the whole search: the answer is about the SOURCE picture, and
					// scaling never introduces or removes an alpha channel.
					alpha ??= canvasHasAlpha(context, width, height);
					return alpha;
				},
			});
		},
		close() {
			bitmap.close();
		},
	};
}

/** Whether any pixel is non-opaque. Stops at the first one it finds. */
function canvasHasAlpha(
	context: OffscreenCanvasRenderingContext2D,
	width: number,
	height: number,
): boolean {
	const {data} = context.getImageData(0, 0, width, height);
	for (let at = 3; at < data.length; at += 4) {
		if (data[at] !== 255) {
			return true;
		}
	}
	return false;
}
